import {Component, inject, OnInit} from '@angular/core';
import {SessionDto} from '../../../core/models/session/sessiondto';
import {RoleConstants} from '../../../core/constants/role.constants';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {AuthService} from '../../../core/services/auth.service';
import {CommonService} from '../../../core/api/services/utility-services/common.service';
import {MenuConstants} from "../../../core/constants/menu.constants";
import {UrlConstants} from "../../../core/constants/url.constants";
import {LocaleService} from '../../../core/services/locale.service';
import {FormType} from '../../../core/constants/form-type.constants';
import {NavigationPanelService} from '../navigation-panel/navigation-panel.service';
import {MenuItemsContainer} from '../menu-item/menu-items-container';
import {EventTypeConstants} from '../../../core/constants/event-type.constants';
import {EventLogReferrerConstants} from '../../../core/constants/event-log-referrer.constants';
import {AccountService} from '../../../core/api/services/account/account.service';
import {AccountStateService} from '../../../core/api/services/account/account-state.service';
import {SpinnerService} from '../../../core/services/spinner.service';
import {GlobalSpinnerComponent} from '../../common/global-spinner/global-spinner.component';

@Component({
  selector: 'nav-bar',
  imports: [
    TranslatePipe,
    GlobalSpinnerComponent
  ],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.css'
})
export class NavigationBarComponent implements OnInit {

  sessionDetails: SessionDto;
  isCustomer: boolean;
  isPartner: boolean;
  readonly commonService = inject(CommonService);
  readonly authService = inject(AuthService);
  readonly MenuConstants = MenuConstants;
  readonly router = inject(Router);
  readonly localeService = inject(LocaleService);
  readonly translateService = inject(TranslateService);
  readonly navigationPanelService = inject(NavigationPanelService);
  readonly accountService = inject(AccountService);
  readonly accountStateService = inject(AccountStateService);
  readonly spinner = inject(SpinnerService);

  showMenu: boolean;
  showLanguageMenu: boolean;

  isOpen = false;
  accountsOpen = false

  contactUsUrl: string;

  ngOnInit() {
    this.commonService.sessiondetails?.subscribe(res => {
      this.sessionDetails = res
      this.isCustomer = this.accessible(RoleConstants.CUSTOMER);
      this.isPartner = this.accessible(RoleConstants.PARTNER);
      this.contactUsUrl = this.isCustomer ? UrlConstants.contactUsCustomerUrl : (this.isPartner ? UrlConstants.contactUsPartnerUrl : UrlConstants.contactUsCommonUrl);
    })
  }


  openNav() {
    this.isOpen = true;
    document.body.style.overflow = 'hidden';
    this.navigationPanelService.open({
      formType: FormType.HEALTH_FAIR,
      title: '',
      menuItems: this.visiblePanelMenuItems,
    });
  }
  closeNav() { this.isOpen = false; document.body.style.overflow = ''; }


  toArray(x: any) { return Array.isArray(x) ? x : (x ? [x] : []); }

  hasAccess(required: any) {
    const req = this.toArray(required);
    if (req.length === 0) return true;
    return req.every(r => this.sessionDetails.userRoles.includes(r));
  }

  accessible(role: string): boolean {
    return this.sessionDetails && this.sessionDetails.userRoles?.includes(role);
  }

  getStarted() {
    this.commonService.logEvent(EventTypeConstants.VIEW_FAQ, 'User is viewing the FAQ and Help resource', '', EventLogReferrerConstants.NAVIGATION);
    window.open('/crcWeb/file/' + this.localeService.getLocaleStr('_') + '/featured-items/Home-Ads/HS_CRCInstruction.pdf', '_blank');
  }

  exitCustomerView() {
    this.spinner.show();
    this.accountService.openCRCPartner(this.sessionDetails.partnerContactId, this.sessionDetails.userName).subscribe(value => {
      if(value.success){
        this.accountStateService.setIsSetFilters(false);
        this.spinner.hide();
        this.commonService.session().subscribe(() => {
          this.router.navigateByUrl(UrlConstants.accountSearchPartnerUrl);
        })
      }
    })
  }

  contactUs() {
    this.router.navigate([this.contactUsUrl, { referrer: EventLogReferrerConstants.NAVIGATION }]);
  }

  displayMenu(menuitem:any):boolean{
    if(menuitem.label===MenuConstants.HOME){
        return true;
    }
    else if(menuitem.path===MenuConstants.ACCOUNTS){
        return this.accessible(RoleConstants.VIEW_PARTNER_ACCOUNTS)||this.accessible(RoleConstants.VIEW_PARTNER_SALES_OFFICES)||this.accessible(RoleConstants.VIEW_PARTNER_USERS);
    }
    else if(menuitem.label===MenuConstants.DASHBOARD){
        const hasRole = typeof menuitem.role==="string" ? this.accessible(menuitem.role) : false;

        // Two-level permission check for dashboard visibility (DM-922):
        // 1. eapOnSite: Account-level flag - organization must have dashboard feature
        // 2. hasDataVisualizationAccess: User-level flag - individual must have permission
        // Dashboard visible only when user has role AND both flags are true
        const hasDashboardAccess = this.sessionDetails?.eapOnSite && this.sessionDetails?.hasDataVisualizationAccess;
        return hasRole && hasDashboardAccess;
    }
    else if (typeof menuitem.role==="string"){
        return this.accessible(menuitem.role);
    }else {
        let accessibleItemCount = 0;
        menuitem.role.forEach((item:string)=>{
            if(this.accessible(item)){
                accessibleItemCount++;
            }
        })
        return menuitem.role.length ===accessibleItemCount;
    }
  }

  selectLocale(localeId:number) {
    const locale = this.localeService.getLocaleForId(localeId);
    if(locale && locale != this.localeService.getLocaleStr()) {
      this.localeService.setLocale(locale)
      this.translateService.use(this.localeService.getLocaleStrForTranslations());
      this.reloadCurrentRoute();
    }
  }

  reloadCurrentRoute(): void {
    let currentUrl = this.router.url;
    const idx = currentUrl.lastIndexOf(';');
    if(idx > 0) {
      currentUrl = currentUrl.substring(0, idx);
    }
    this.router.navigateByUrl('common/contactUs', {skipLocationChange: true}).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  enterLanguage() {
    this.showLanguageMenu = true;
  }

  leaveLanguage() {
    setTimeout(() => {this.showLanguageMenu = false}, 3000)
  }

  get visiblePanelMenuItems() {
    // Dedupe identical path+label if needed
    const seen = new Set<string>();

    return MenuItemsContainer.PANEL_MENU_ITEMS.filter(i => {
      if (!this.hasAccess(i.role)) return false;
      const key = `${i.path}|${i.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      if(i.label == MenuConstants.PROGRAMS && !this.sessionDetails.hasProgramModels) return false;
      return true;
    });
  }

  home() {
    this.router.navigate([UrlConstants.homeUrl]);
  }

  protected readonly RoleConstants = RoleConstants;
}
